import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  Comparaison,
  ExposureResult,
  GroupCode,
  MasteryResult,
  TaskLevel,
  TimeCostResult,
} from "@riseafrica/scoring";
import { lireParrainage, lireReglages } from "../lib/api";
import { lireAchat, memoriserAchat, oublierAchat } from "../lib/achat";
import type { AchatMemorise, PlanAchat } from "../lib/achat";
import type { QuizQuestion } from "../content/copy";
import type {
  EtatParrainage,
  PackAction,
  PackTask,
  ProfilEntreprisePourRapport,
} from "../lib/api";
import { track } from "../lib/track";
import { chargerPixel, definirMetaActif } from "../lib/meta";
import { CONFIG } from "../config";
import { aSonCompteDepuisCampagne, campagneCourante } from "../lib/campagneCourante";

export type ViewId =
  | "home"
  | "s-t1"
  | "s-scan"
  | "s-tasks"
  | "s-r1"
  | "s-t2"
  | "s-r2"
  | "s-t3"
  | "s-r3";

/** Une tâche retenue par l'utilisateur, avec le temps qu'il déclare y passer. */
export interface SelectedTask extends PackTask {
  heures?: number;
}

/** Volet 1 — résultat complet, calculé par le module de scoring. */
export interface Volet1 {
  exposure: ExposureResult;
  metier: string;
  taches: SelectedTask[];
  actions: PackAction[];
  /** Volet 3 réécrit pour ce métier. Absent en repli : le générique s'applique. */
  situations?: QuizQuestion[];
  fromCv: boolean;
  degraded: boolean;
  /** Trace d'exécution, affichée dans le panneau technique. */
  meta: { cacheHit: boolean; mode: string };
}

export interface Volet2 extends TimeCostResult {
  /**
   * Heures de gestion déclarées, dans l'ordre de `GESTION_TASKS` — T38. Seul
   * quelqu'un à son compte les déclare ; absentes sinon.
   */
  heuresGestion?: number[];
  /**
   * La valeur annuelle **sans** la gestion. Si la personne décoche « à mon
   * compte » au moment de payer, son rapport devient celui d'un salarié, et
   * sa valeur ne doit plus compter un temps de gestion qu'il ne verra pas.
   */
  moneyMetier?: number;
}

export interface Volet3 extends MasteryResult {
  nom: string;
  /*
    `null` quand l'émission n'a pas abouti.

    Le numéro était auparavant fabriqué par le navigateur et n'existait nulle
    part : toute vérification le refusait. Il vient désormais du serveur, qui
    l'enregistre — et s'il ne répond pas, on n'affiche **aucun** numéro. Une
    attestation sans numéro est une déception ; un numéro qui ne se vérifie pas
    est un mensonge.
  */
  serial: string | null;
  /** Écart avec l'attestation précédente, quand la personne l'a présentée. */
  progression: Progression | null;
  date: string;
}

/** Ce qu'on a gagné depuis la fois d'avant. Calculé par le serveur. */
export interface Progression {
  precedent: string;
  pointsAvant: number;
  pointsApres: number;
  ecart: number;
  niveauAvant: string;
  niveauApres: string;
  jours: number;
}

/**
 * Les trois offres. `entreprise` est celle de l'axe dirigeant, à 15 000 F —
 * arbitrage du 18/08. Le montant n'est pas ici : il vit dans le module partagé,
 * et le serveur le recalcule sans jamais faire confiance au navigateur.
 */
export type PayPlan = "report" | "bundle" | "entreprise";

interface AppContextValue {
  view: ViewId;
  goTo: (view: ViewId) => void;
  t1: Volet1 | null;
  t2: Volet2 | null;
  t3: Volet3 | null;
  setT1: (value: Volet1) => void;
  setT2: (value: Volet2) => void;
  setT3: (value: Volet3) => void;
  /** Métier retenu, partagé entre les volets. */
  metier: string | null;
  /**
   * La personne travaille à son compte — arbitrage du 25/08/2026.
   *
   * Posé **à l'entrée**, et non au paiement comme la première version le
   * faisait. La case au paiement réparait le document ; elle ne réparait pas la
   * vente. Un indépendant parcourait un écran de résultat écrit pour un
   * salarié, puis une offre qui lui promettait « la trajectoire de
   * repositionnement la plus courte » — c'est-à-dire exactement ce qui ne le
   * concerne pas — et ne rencontrait la case qu'après avoir décidé d'acheter.
   *
   * Ce que ce booléen change : la lecture proposée à côté de l'indice, ce que
   * l'offre promet, et une section du rapport. **Ce qu'il ne change pas :
   * l'indice lui-même.** Si « exposition » devenait « opportunité » selon le
   * statut, il y aurait deux échelles, et les chiffres publiés au baromètre
   * cesseraient d'être comparables.
   */
  aSonCompte: boolean;
  setASonCompte: (valeur: boolean) => void;
  /**
   * Le statut a-t-il été **déclaré**, ou seulement laissé à sa valeur par
   * défaut — 26/08/2026.
   *
   * `aSonCompte` seul ne permet pas de faire la différence : « salarié
   * déclaré » et « rien déclaré » valent tous les deux `false`. Le paiement en
   * avait besoin. Il reposait la question à blanc — « Cochez si vous êtes
   * indépendant » — à quelqu'un qui venait de la trancher à l'accueil, sur le
   * seul écran où une friction coûte de l'argent.
   *
   * Ce drapeau ne se lève qu'à une déclaration explicite, et il ne redescend
   * jamais : corriger son statut au paiement reste une déclaration.
   */
  statutDeclare: boolean;
  /** Déclare le statut. Pose la valeur **et** lève `statutDeclare`. */
  declarerStatut: (aSonCompte: boolean) => void;
  /**
   * Parrainage mesuré côté serveur : filleuls ayant réellement **complété** un
   * diagnostic. `null` tant que le serveur n'a pas répondu.
   */
  parrainage: EtatParrainage | null;
  /** Nombre de partages émis depuis cet écran. Ce que l'utilisateur a fait. */
  partages: number;
  registerShare: () => void;
  /** Redemande l'état du parrainage au serveur. */
  rafraichirParrainage: () => void;
  /** Remplace la comparaison par celle calculée sur les résultats réels. */
  majComparaison: (comparaison: Comparaison) => void;
  /** Annonce un résultat dans la région `aria-live`. */
  say: (message: string) => void;
  liveMessage: string;
  payPlan: PayPlan | null;
  openPay: (plan: PayPlan) => void;
  closePay: () => void;
  /** Prix du rapport. Unique : aucune remise ne dépend du comportement. */
  reportPrice: number;
  /**
   * L'achat déjà engagé, relu au démarrage dans le stockage du navigateur.
   *
   * Tant qu'il vaut `null`, le produit propose d'acheter. Dès qu'il porte une
   * transaction, les écrans cessent de solliciter l'acheteur et lui offrent de
   * récupérer ce qu'il a payé.
   */
  achat: AchatMemorise | null;
  noterAchat: (tx: string, plan: PlanAchat) => void;
  oublierLAchat: () => void;
  /**
   * Profil de l'axe dirigeant, déposé par l'écran `/dirigeant` quand un
   * résultat est affiché. La fenêtre de paiement le lit pour le plan
   * `entreprise` : sans lui, le serveur refuse la vente plutôt que d'encaisser
   * un rapport qu'il ne pourra pas rédiger. Aucun champ d'argent n'y figure.
   */
  entreprise: ProfilEntreprisePourRapport | null;
  setEntreprise: (profil: ProfilEntreprisePourRapport | null) => void;
  /**
   * Les dix titres de l'action offerte et des neuf verrouillées, déposés par
   * l'écran de résultat quand le plan est arrivé — 06/09/2026. La fenêtre de
   * paiement les joint au profil : le rapport doit tenir ce qui a été montré.
   */
  titresPlan: string[] | null;
  setTitresPlan: (titres: string[] | null) => void;
  /**
   * Le profil d'un rapport de progression, déposé par la page de progression
   * — 28/09/2026. Quand il est posé, la fenêtre de paiement l'envoie tel quel
   * au lieu de reconstruire un profil depuis le parcours : la personne arrive
   * d'un lien, sans avoir refait le diagnostic.
   */
  profilProgression: ProfilProgressionAchat | null;
  setProfilProgression: (profil: ProfilProgressionAchat | null) => void;
  /**
   * Le mode « CV collé » du volet 1 est-il ouvert ?
   *
   * Trois états, et la distinction compte : `null` tant que le serveur n'a pas
   * répondu, `true` ouvert, `false` fermé depuis `/pilotage`. Traiter l'inconnu
   * comme un « fermé » retirerait un mode qui marche à qui a une connexion
   * lente ; le traiter comme un « ouvert » proposerait un écran que le serveur
   * refusera. Les écrans n'affichent donc rien tant qu'ils ne savent pas.
   */
  modeCv: boolean | null;
  /**
   * La formule à 9 000 F est-elle assurée, selon le serveur ?
   *
   * Faux tant qu'il n'a pas répondu, et faux par défaut — à l'inverse du mode
   * CV, dont l'inconnu n'affiche rien. Proposer une carte que `/pay/init`
   * refusera coûte plus qu'attendre une réponse : la personne décide de payer,
   * puis se voit refuser. Le drapeau vivait auparavant dans le paquet construit
   * (`VITE_ACCOMPAGNEMENT`), si bien que l'interrupteur de `/pilotage`
   * n'atteignait jamais l'écran.
   */
  accompagnementActif: boolean;
  /** Le suivi de progression est-il allumé ? Faux tant que le serveur n'a rien dit. */
  progressionActive: boolean;
  /**
   * Le prix du rapport dirigeant, tel que le serveur le déclare.
   *
   * `null` tant qu'il n'a pas répondu — les écrans retombent alors sur le prix
   * du module partagé, qui est la bonne valeur dans le cas ordinaire. Afficher
   * un prix vide ou nul en attendant serait pire que d'afficher l'ancien.
   */
  prixEntreprise: number | null;
  /**
   * L'adresse à laquelle le public écrit, telle que le serveur la déclare.
   *
   * Elle était écrite en dur dans le paquet : la changer demandait une
   * livraison de code, alors qu'elle figure sous la garantie de remboursement
   * et sous l'exercice des droits — les deux endroits où quelqu'un écrit parce
   * qu'il est mécontent ou inquiet. Tant que le serveur n'a pas répondu, celle
   * du paquet fait foi : jamais d'adresse vide sous une promesse.
   */
  contactCourriel: string;
  /**
   * Le délai de conservation d'un numéro, tel que la purge l'applique.
   *
   * Zéro veut dire « inconnu » : la page annonce alors une mention en attente
   * plutôt qu'un délai. C'est la même posture que pour l'adresse de contact —
   * jamais de valeur inventée sous un engagement.
   */
  conservationContactsJours: number;
  /** Les mentions légales posées en exploitation, sous leurs clés serveur. */
  mentionsPosees: Record<string, string>;
  /** Ce que le tunnel de paiement exige, connu avant la première soumission. */
  identiteAcheteurRequise: boolean;
  codeAccesRequis: boolean;
  /** Meta autorisé par l'exploitation (T43) : décide du bandeau de consentement. */
  metaActif: boolean;
  /** L'axe dirigeant ouvert par l'exploitation : décide de ses portes d'entrée. */
  dirigeantActif: boolean;
  /**
   * Les réglages ont répondu, ou échoué — 11/10/2026. Avant, `/dirigeant`
   * montrait le formulaire une fraction de seconde sur un axe fermé, et
   * comptait une entrée qui n'avait pas eu lieu.
   */
  reglagesLus: boolean;
}

/** Le profil envoyé au paiement d'un rapport de progression. */
export interface ProfilProgressionAchat {
  metier: string;
  metierNormalise: string;
  groupe: GroupCode;
  taches: Array<{ texte: string; niveau: TaskLevel; heures: number }>;
  valeurAnnuelle: null;
  maitrise: null;
  aSonCompte: boolean;
  gestion: number[] | null;
  titres: null;
  progression: { jeton: string; minutes: Array<number | null> };
}

const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp doit être utilisé dans AppProvider");
  return value;
}

/**
 * Ce que l'on inscrit dans l'historique du navigateur à chaque étape.
 *
 * Le parcours n'y laissait aucune trace : sur Android, où le retour est un
 * bouton matériel utilisé sans y penser, une personne au milieu de son
 * diagnostic **quittait le site** d'une pression. Sur un produit conçu pour le
 * mobile d'abord et dont l'essentiel du trafic arrive par un lien WhatsApp,
 * c'était une fuite silencieuse à chaque écran.
 *
 * L'adresse ne change pas : le parcours n'a pas d'URL propre, et il ne doit pas
 * en avoir — un résultat partagé par copie de la barre d'adresse serait un
 * résultat publié sans qu'on l'ait voulu. Seule l'entrée d'historique est
 * empilée.
 */
interface EtatHistorique {
  vue: ViewId;
  modale: PayPlan | null;
}

function empiler(etat: EtatHistorique): void {
  window.history.pushState(etat, "");
}

/*
   L'écran d'analyse ne laisse pas de trace dans l'historique.

   Il en laissait une, et revenir depuis l'écran des tâches y ramenait : une
   barre de progression qui repartait de zéro pour une analyse déjà terminée,
   sans bouton, sans message, sans fin. Une impasse déguisée en attente — la
   pire espèce, parce qu'elle a l'air normale.

   Une attente n'est pas une destination : on ne revient pas dans un couloir.
   Le retour depuis les tâches ramène donc là d'où l'on est parti.
  */
const TRANSITOIRES = new Set<ViewId>(["s-scan"]);

export function AppProvider({ children, price }: { children: ReactNode; price: number }) {
  const [view, setView] = useState<ViewId>("home");
  const [t1, setT1] = useState<Volet1 | null>(null);
  const [t2, setT2] = useState<Volet2 | null>(null);
  const [t3, setT3] = useState<Volet3 | null>(null);
  const [parrainage, setParrainage] = useState<EtatParrainage | null>(null);
  const [partages, setPartages] = useState(0);
  const [liveMessage, setLiveMessage] = useState("");
  const [payPlan, setPayPlan] = useState<PayPlan | null>(null);
  // Relu une seule fois : le stockage ne change pas sous nos pieds pendant la
  // visite, et le lire à chaque rendu coûterait un accès disque par affichage.
  const [achat, setAchat] = useState<AchatMemorise | null>(() => lireAchat());
  const [entreprise, setEntreprise] = useState<ProfilEntreprisePourRapport | null>(null);
  const [titresPlan, setTitresPlan] = useState<string[] | null>(null);
  const [profilProgression, setProfilProgression] = useState<ProfilProgressionAchat | null>(null);
  const [modeCv, setModeCv] = useState<boolean | null>(null);
  // La valeur du paquet sert d'amorce — elle reste utile pour une construction
  // dédiée — et la réponse du serveur la remplace dès qu'elle arrive.
  const [accompagnementActif, setAccompagnementActif] = useState(CONFIG.accompagnementActif);
  const [progressionActive, setProgressionActive] = useState(false);
  // Préréglé par l'annonce d'arrivée, sans valoir déclaration : le statut
  // reste à confirmer, et un geste à l'accueil le change.
  const [aSonCompte, setASonCompte] = useState(() => aSonCompteDepuisCampagne(campagneCourante()));
  const [statutDeclare, setStatutDeclare] = useState(false);
  const declarerStatut = useCallback((valeur: boolean) => {
    setASonCompte(valeur);
    setStatutDeclare(true);
  }, []);
  const [prixEntreprise, setPrixEntreprise] = useState<number | null>(null);
  const [contactCourriel, setContactCourriel] = useState<string>(CONFIG.contactEmail);
  const [conservationContactsJours, setConservationContactsJours] = useState(0);
  const [mentionsPosees, setMentionsPosees] = useState<Record<string, string>>({});
  const [identiteAcheteurRequise, setIdentiteAcheteurRequise] = useState(false);
  const [codeAccesRequis, setCodeAccesRequis] = useState(false);
  const [metaActif, setMetaActif] = useState(false);
  // Ouvert tant que le serveur n'a rien dit : c'est l'état d'avant le réglage.
  const [dirigeantActif, setDirigeantActif] = useState(true);
  const [reglagesLus, setReglagesLus] = useState(false);
  const ouvertureAnnoncee = useRef(false);

  const say = useCallback((message: string) => setLiveMessage(message), []);

  // La vue courante, lisible depuis un gestionnaire d'évènement sans le
  // recréer à chaque changement d'écran.
  const vueCourante = useRef<ViewId>("home");
  vueCourante.current = view;

  const goTo = useCallback((next: ViewId) => {
    // Les pages hors parcours (méthode, vérification d'attestation) partagent
    // l'en-tête et le pied de page. Un lien de navigation cliqué depuis
    // l'une d'elles doit ramener à l'application, pas changer un état que
    // personne n'affiche.
    if (window.location.pathname !== "/") {
      window.location.assign("/");
      return;
    }
    setView(next);
    setPayPlan(null);
    if (!TRANSITOIRES.has(next)) empiler({ vue: next, modale: null });
    window.scrollTo(0, 0);
    track("view", { screen: next });
  }, []);

  /**
   * Le retour du navigateur rejoue l'état empilé. Sans entrée enregistrée, on
   * est revenu à l'arrivée sur le site : c'est l'accueil, et un retour de plus
   * quitte le site — ce qui est le comportement attendu depuis l'accueil, et
   * seulement depuis lui.
   */
  useEffect(() => {
    const auRetour = (evenement: PopStateEvent) => {
      const etat = evenement.state as EtatHistorique | null;
      setView(etat?.vue ?? "home");
      setPayPlan(etat?.modale ?? null);
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", auRetour);
    return () => window.removeEventListener("popstate", auRetour);
  }, []);

  /**
   * Le parrainage ne touche pas au prix : l'acquisition organique est le seul
   * canal où l'économie du produit tient, et lui faire payer la moitié du
   * revenu revient à taxer sa propre croissance.
   *
   * **Ce qu'il ouvre, depuis le 31/08/2026 : le second diagnostic.** Ce
   * commentaire disait jusqu'ici l'inverse — « le diagnostic est librement
   * répétable et aucune ligne du produit ne pose de limite », et il renvoyait le
   * porteur à un arbitrage « pas encore tranché ». Il l'a été le 31/08 : la
   * limite existe, elle est portée par `functions/src/session.ts`, et un second
   * diagnostic demandé avant terme reçoit un `409`. La phrase était vraie le
   * jour où elle a été écrite, et rien ne l'a touchée quand le produit a changé
   * dessous — exactement le défaut trouvé la veille sur la page méthode.
   *
   * Le décompte reste ce qu'il était : **deux filleuls distincts ayant complété
   * un diagnostic**, comptés par le serveur. Le nombre de partages émis n'entre
   * pas dans ce chiffre — compter les appuis sur un bouton et les présenter
   * comme des personnes ayant passé le test serait un faux signal au sens de la
   * règle n° 6.
   *
   * @param annoncer Faut-il porter l'ouverture à la région `aria-live` ? Non au
   *   démarrage : un parrainage **déjà** ouvert quand la page se charge n'est
   *   pas un évènement, et l'annoncer sortirait une phrase de nulle part à qui
   *   navigue à l'oreille. Oui après un partage, où c'est précisément une
   *   nouvelle.
   */
  const lireEtatParrainage = useCallback((annoncer: boolean) => {
    void lireParrainage().then((etat) => {
      if (!etat) return;
      setParrainage(etat);
      if (!etat.ouvert || ouvertureAnnoncee.current) return;
      ouvertureAnnoncee.current = true;
      if (!annoncer) return;
      // On annonce le fait, pas une récompense : deux personnes ont passé le
      // test depuis ce lien, et c'est vérifiable côté serveur.
      setLiveMessage(`${etat.filleuls} personnes ont passé le test depuis votre lien`);
    });
  }, []);

  const rafraichirParrainage = useCallback(() => {
    lireEtatParrainage(true);
  }, [lireEtatParrainage]);

  /**
   * Une lecture au démarrage, pour que l'accueil sache ce qu'il en est.
   *
   * Sans elle, l'état du second diagnostic n'était connu qu'**après** l'appel à
   * `/diagnostic` : une session revenue avant terme choisissait son mode,
   * tapait son métier — ou collait son CV en entier — pressait le bouton,
   * attendait, et recevait alors seulement le refus. Le serveur avait raison
   * depuis le début ; c'est l'écran qui ne le demandait jamais à temps.
   *
   * Silencieuse comme celle des réglages : si elle échoue, `parrainage` reste
   * `null`, l'accueil n'affiche rien de particulier et le refus retombe sur le
   * chemin d'erreur, qui le dit correctement. Ne pas savoir ne ferme rien.
   */
  useEffect(() => {
    lireEtatParrainage(false);
  }, [lireEtatParrainage]);

  const registerShare = useCallback(() => {
    setPartages((n) => {
      track("share", { count: n + 1 });
      return n + 1;
    });
    // Le filleul peut compléter son diagnostic à tout moment : on redemande
    // l'état au serveur, sans jamais le déduire du partage lui-même.
    rafraichirParrainage();
  }, [rafraichirParrainage]);

  const majComparaison = useCallback((comparaison: Comparaison) => {
    setT1((actuel) =>
      actuel === null ? actuel : { ...actuel, exposure: { ...actuel.exposure, comparaison } },
    );
  }, []);

  /*
    La fenêtre de paiement prend sa propre entrée d'historique : un retour la
    referme au lieu de faire sortir du parcours. C'est ce que le geste veut dire
    quand une fenêtre est ouverte, et c'est ce qu'un utilisateur d'Android
    attend sans avoir à y penser.
  */
  const openPay = useCallback((plan: PayPlan) => {
    setPayPlan(plan);
    empiler({ vue: vueCourante.current, modale: plan });
  }, []);

  /*
    Retenu dès l'ouverture de la session de paiement, et non à sa confirmation.
    C'est précisément entre les deux que tout se perd : l'utilisateur part chez
    l'opérateur, revient sur une page rechargée, et n'a plus rien à montrer.
  */
  const noterAchat = useCallback((tx: string, plan: PlanAchat) => {
    const trace: AchatMemorise = { tx, plan, quand: Date.now() };
    memoriserAchat(trace);
    setAchat(trace);
  }, []);

  const oublierLAchat = useCallback(() => {
    oublierAchat();
    setAchat(null);
  }, []);

  const closePay = useCallback(() => {
    const etat = window.history.state as EtatHistorique | null;
    // Fermée par la croix : on retire l'entrée de la fenêtre plutôt que de la
    // laisser derrière, sans quoi un retour la rouvrirait.
    if (etat?.modale != null) window.history.back();
    else setPayPlan(null);
  }, []);

  /**
   * Une lecture, au démarrage, de ce que l'exploitation a ouvert ou fermé.
   *
   * Elle est **délibérément silencieuse** : si elle échoue, `modeCv` reste
   * inconnu et les écrans s'en tiennent à ce qui marche sans elle. Un réglage
   * d'affichage n'a pas le droit de faire échouer une page.
   */
  useEffect(() => {
    let vivant = true;
    void lireReglages().then((reglages) => {
      if (!vivant) return;
      setReglagesLus(true);
      if (!reglages) return;
      setModeCv(reglages.modeCv);
      setAccompagnementActif(reglages.accompagnementActif);
      setProgressionActive(reglages.progressionActive);
      if (reglages.prixEntreprise > 0) setPrixEntreprise(reglages.prixEntreprise);
      if (reglages.contactCourriel) setContactCourriel(reglages.contactCourriel);
      if (reglages.conservationContactsJours > 0)
        setConservationContactsJours(reglages.conservationContactsJours);
      setMentionsPosees(reglages.mentions);
      setIdentiteAcheteurRequise(reglages.identiteAcheteurRequise);
      setCodeAccesRequis(reglages.codeAccesRequis);
      // Meta (T43) : l'exploitation garde la main ; un accord déjà donné
      // recharge le pixel dès que l'on sait qu'il est autorisé.
      definirMetaActif(reglages.metaActif);
      setMetaActif(reglages.metaActif);
      setDirigeantActif(reglages.dirigeantActif);
      chargerPixel();
    });
    return () => {
      vivant = false;
    };
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      view,
      goTo,
      t1,
      t2,
      t3,
      setT1,
      setT2,
      setT3,
      metier: t1?.metier ?? null,
      aSonCompte,
      setASonCompte,
      statutDeclare,
      declarerStatut,
      parrainage,
      partages,
      registerShare,
      rafraichirParrainage,
      majComparaison,
      say,
      liveMessage,
      payPlan,
      openPay,
      closePay,
      achat,
      noterAchat,
      oublierLAchat,
      entreprise,
      setEntreprise,
      titresPlan,
      setTitresPlan,
      profilProgression,
      setProfilProgression,
      modeCv,
      accompagnementActif,
      progressionActive,
      prixEntreprise,
      contactCourriel,
      conservationContactsJours,
      mentionsPosees,
      identiteAcheteurRequise,
      codeAccesRequis,
      metaActif,
      dirigeantActif,
      reglagesLus,
      reportPrice: price,
    }),
    [
      view,
      goTo,
      t1,
      t2,
      t3,
      parrainage,
      partages,
      registerShare,
      rafraichirParrainage,
      majComparaison,
      say,
      liveMessage,
      payPlan,
      openPay,
      closePay,
      achat,
      noterAchat,
      oublierLAchat,
      entreprise,
      profilProgression,
      modeCv,
      accompagnementActif,
      progressionActive,
      prixEntreprise,
      contactCourriel,
      conservationContactsJours,
      mentionsPosees,
      identiteAcheteurRequise,
      codeAccesRequis,
      metaActif,
      dirigeantActif,
      reglagesLus,
      aSonCompte,
      statutDeclare,
      declarerStatut,
      price,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
